import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/* A compact, themed SINGLE-date calendar (no external deps).
   The one-day sibling of `RangeCalendar`, and deliberately identical to it cell
   for cell: same S-M-T-W-T-F-S header, same 6×7 grid with the adjacent months
   greyed in, same green pill on the picked day and hairline ring on today. The
   leads filter drawer and the payment form now show the SAME calendar — before
   this they showed two, and the difference read as one of them being broken.

   Values are yyyy-mm-dd strings, built from integer y/m/d and never through
   `new Date("2026-08-20")`. That parse is UTC midnight, so west of Greenwich
   `.getDate()` on it hands back the 19th — a payment silently dated a day early.
   String comparison on this format is chronologically correct, so no Date math
   is needed anywhere below. */

/** Tiny dependency-free className joiner (avoids twMerge mangling the arbitrary
    color/rounded classes used below). */
const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(" ");

const WD = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n: number) => String(n).padStart(2, "0");
const toIso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

/** "YYYY-MM-DD" -> integer parts. Null on anything else, including "". */
function parseIso(value: string): { y: number; m: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]) - 1 };
}

interface DayCell {
  iso: string;
  day: number;
  inMonth: boolean;
}

export interface SingleDateCalendarProps {
  /** "YYYY-MM-DD", or "" for nothing picked. */
  date: string;
  onDateChange: (date: string) => void;
  /** "YYYY-MM-DD" — days after this are shown but not selectable. */
  maxDate?: string;
  /** Month + year dropdowns in the header instead of the plain title, for
   *  dates far from today (a date of birth). Off = the calendar as before. */
  yearPicker?: boolean;
}

/** First year the year dropdown offers. */
const YEAR_PICKER_FROM = 1900;

export function SingleDateCalendar({
  date,
  onDateChange,
  maxDate,
  yearPicker = false,
}: SingleDateCalendarProps) {
  const today = useMemo(() => new Date(), []);
  const todayIso = toIso(today.getFullYear(), today.getMonth(), today.getDate());

  // Open on the picked month if there is one, else the current month. With the
  // year picker, a limit already behind us opens on the limit's month instead.
  const [view, setView] = useState<{ y: number; m: number }>(() => {
    const parsed = parseIso(date);
    if (parsed) return parsed;
    const limit = yearPicker && maxDate ? parseIso(maxDate) : null;
    if (limit && maxDate! < todayIso) return limit;
    return { y: today.getFullYear(), m: today.getMonth() };
  });

  // Newest first: the dropdown opens near today. Runs to the limit's year, or
  // 50 years ahead with no limit; always holds the year on screen.
  const years = useMemo(() => {
    if (!yearPicker) return [];
    const last = maxDate ? Number(maxDate.slice(0, 4)) : today.getFullYear() + 50;
    const out: number[] = [];
    for (let y = Math.max(last, view.y); y >= Math.min(YEAR_PICKER_FROM, view.y); y--) {
      out.push(y);
    }
    return out;
  }, [yearPicker, maxDate, today, view.y]);

  const cells = useMemo<DayCell[]>(() => {
    const { y, m } = view;
    const firstWeekday = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const daysPrev = new Date(y, m, 0).getDate();
    const out: DayCell[] = [];
    for (let i = firstWeekday - 1; i >= 0; i--) {
      const d = daysPrev - i;
      const pm = m === 0 ? 11 : m - 1;
      const py = m === 0 ? y - 1 : y;
      out.push({ iso: toIso(py, pm, d), day: d, inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      out.push({ iso: toIso(y, m, d), day: d, inMonth: true });
    }
    let nd = 1;
    while (out.length < 42) {
      const nm = m === 11 ? 0 : m + 1;
      const ny = m === 11 ? y + 1 : y;
      out.push({ iso: toIso(ny, nm, nd), day: nd, inMonth: false });
      nd++;
    }
    return out;
  }, [view]);

  const prev = () =>
    setView((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }));
  const next = () =>
    setView((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }));

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        {yearPicker ? (
          <div className="flex items-center gap-1">
            <select
              aria-label="Month"
              value={view.m}
              onChange={(e) => setView((v) => ({ y: v.y, m: Number(e.target.value) }))}
              className="h-7 rounded-md border border-[#e0e5e3] bg-white px-1 text-[13px] font-semibold text-[#1e302a] focus-visible:outline-2 focus-visible:outline-[#218760]"
            >
              {MONTHS.map((name, i) => (
                <option key={name} value={i}>
                  {name}
                </option>
              ))}
            </select>
            <select
              aria-label="Year"
              value={view.y}
              onChange={(e) => setView((v) => ({ y: Number(e.target.value), m: v.m }))}
              className="h-7 rounded-md border border-[#e0e5e3] bg-white px-1 text-[13px] font-semibold tabular-nums text-[#1e302a] focus-visible:outline-2 focus-visible:outline-[#218760]"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <span className="text-[13px] font-semibold text-[#1e302a]">
            {MONTHS[view.m]} {view.y}
          </span>
        )}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={prev}
            aria-label="Previous month"
            className="grid size-7 place-items-center rounded-full text-[#697571] transition-colors hover:bg-[#eaf5f1] hover:text-[#051912]"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next month"
            className="grid size-7 place-items-center rounded-full text-[#697571] transition-colors hover:bg-[#eaf5f1] hover:text-[#051912]"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7">
        {WD.map((w, i) => (
          <span
            key={i}
            className="grid h-7 place-items-center text-[11px] font-medium text-[#9ba3a0]"
          >
            {w}
          </span>
        ))}
        {cells.map((c) => {
          const isSelected = c.iso === date;
          const isToday = c.iso === todayIso;
          // ISO "YYYY-MM-DD" strings compare lexicographically as dates.
          const isDisabled = Boolean(maxDate) && c.iso > (maxDate as string);
          return (
            <div key={c.iso} className="relative grid h-8 place-items-center">
              <button
                type="button"
                disabled={isDisabled}
                onClick={() => onDateChange(c.iso)}
                className={cx(
                  "grid size-8 place-items-center rounded-full text-[12px] tabular-nums transition-colors",
                  isDisabled && "cursor-not-allowed text-[#d5dbd8] line-through",
                  !isDisabled && !c.inMonth && "text-[#c2c9c6] hover:bg-[#eef3f0]",
                  !isDisabled && c.inMonth && !isSelected && "text-[#1e302a] hover:bg-[#d6ebe3]",
                  isSelected &&
                    "bg-[#218760] font-semibold text-white shadow-[0_2px_5px_rgba(33,135,96,0.3)]",
                  !isSelected && isToday && "ring-1 ring-inset ring-[#218760]/40",
                )}
              >
                {c.day}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
